import {ChangeDetectionStrategy, Component, Inject, OnInit, PLATFORM_ID} from '@angular/core';
import {DcpAuthService} from '@app/auth/services/dcp-auth.service';
import {SupportUrl} from '@app/checkout/checkout-support-widget/checkout-support-widget.component';
import {CmsFlexComponentData} from '@app/checkout/order-confirmation/order-confirmation-list-item/order-confirmation-order-item.model';
import {DelegoPaymentErrorService} from '@app/payment/services/delego-rapidpay/error-handling/delego-payment-error.service';
import {DcpProductUtilitiesService} from '@app/product/services/dcp-product-utilities.service';
import {EddlFormViewEvent} from '@app/services/analytics/EDDL/events/eddl-form-view-event';
import {SUPPORT_FORM_NAME} from '@app/services/analytics/EDDL/models/eddl-adobe-tracking-data';
import {DcpActiveCartService} from '@app/services/dcp-active-cart.service';
import {DcpCartDecoratorService} from '@app/services/dcp-cart-decorator.service';
import {CommercialModelService, ConfigService, ConfiguratorUriParameterService, JsonService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {EventService} from '@spartacus/core';
import {OrderDetailsService} from '@spartacus/order/components';
import {CmsComponentData, CurrentProductService} from '@spartacus/storefront';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {fromEvent, Observable, Subscription} from 'rxjs';
import {first, shareReplay, switchMap, throttleTime} from 'rxjs/operators';
import {SupportFormComponent} from '../iframe/support-form.component';
import {DcpSupportFormService} from '../dcp-support-form.service';
import {AsyncPipe} from '@angular/common';
import {SafeResourceUrlPipe} from '../../../pipes/safeResourceUrl/safeResourceUrl.pipe';

export class SupportFormCmsData {
  url: SupportUrl;
}

@UntilDestroy()
@Component({
  selector: 'dcp-support-form-page',
  templateUrl: './support-form-page.component.html',
  styleUrls: ['./support-form-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, SafeResourceUrlPipe],
})
export class SupportFormPageComponent extends SupportFormComponent implements OnInit {
  supportUrl: SupportUrl;
  componentData: SupportFormCmsData;
  componentData$: Observable<CmsFlexComponentData>;
  componentDataSubscription: Subscription = Subscription.EMPTY;
  mouseMove$: Observable<MouseEvent>;
  mouseMoveSubscription$: Subscription = Subscription.EMPTY;
  currentIframeContentHeight: string;

  constructor(
    @Inject(PLATFORM_ID) platformId: object,
    userFacade: UserAccountFacade,
    activeCartService: DcpActiveCartService,
    dcpCartDecoratorService: DcpCartDecoratorService,
    orderDetailsService: OrderDetailsService,
    dcpAuthService: DcpAuthService,
    configuratorUriParameterService: ConfiguratorUriParameterService,
    configService: ConfigService,
    currentProductService: CurrentProductService,
    dcpProductUtilitiesService: DcpProductUtilitiesService,
    delegoPaymentErrorService: DelegoPaymentErrorService,
    private cmsData: CmsComponentData<CmsFlexComponentData>,
    private jsonService: JsonService,
    private eventService: EventService,
    dcpSupportFormService: DcpSupportFormService,
    commercialModelService: CommercialModelService,
  ) {
    super(
      platformId,
      userFacade,
      activeCartService,
      dcpCartDecoratorService,
      orderDetailsService,
      dcpAuthService,
      configuratorUriParameterService,
      configService,
      currentProductService,
      dcpProductUtilitiesService,
      dcpSupportFormService,
      delegoPaymentErrorService,
      commercialModelService,
    );
  }

  ngOnInit() {
    super.ngOnInit();
    this.mapComponentData();
    this.listenMousemoveEvent();
    this.triggerEddlSupportFormViewEvent();
  }

  listenMousemoveEvent() {
    setTimeout(() => {
      const iframeDoc = this.iframeComponent?.nativeElement?.contentDocument;
      if (!iframeDoc) {
        return;
      }
      this.mouseMove$ = fromEvent<MouseEvent>(iframeDoc, 'mousemove');
      this.mouseMoveSubscription$ = this.mouseMove$.pipe(throttleTime(500), untilDestroyed(this)).subscribe(() => {
        if (this.currentIframeContentHeight !== this.getIframeContentHeight()) {
          this.currentIframeContentHeight = this.getIframeContentHeight();
          this.iframeComponent.nativeElement.style.height = this.currentIframeContentHeight;
        }
      });
    });
  }

  getIframeContentHeight(): string {
    return (
      (this.iframeComponent?.nativeElement?.contentWindow?.document?.querySelector('dcp-root')?.scrollHeight ?? 0) +
      'px'
    );
  }

  mapComponentData() {
    this.componentData$ = this.cmsData.data$;
    this.componentDataSubscription = this.componentData$.pipe(untilDestroyed(this)).subscribe((data) => {
      this.componentData = this.jsonService.parseSafely(data?.classificationsProductData) as SupportFormCmsData;
      this.supportUrl = this.componentData.url;
      this.baseUrl = this.supportUrl.link;
      this.url$ = this.dcpAuthService.isUserLoggedIn().pipe(
        first(),
        switchMap((isUserLoggedIn: boolean) => {
          this.isUserLoggedIn = isUserLoggedIn;
          return this.getURL();
        }),
        shareReplay({bufferSize: 1, refCount: true}),
      );
    });
  }

  private triggerEddlSupportFormViewEvent() {
    const event = new EddlFormViewEvent();
    event.name = SUPPORT_FORM_NAME;
    setTimeout(() => this.eventService.dispatch(event));
  }
}
